import { Box, CircularProgress } from "@material-ui/core";
import { styled } from "@material-ui/core/styles";
import PropTypes from "prop-types";
import * as React from "react";
import { useUser } from "../../globalState";
import useAddons, {
  useAvailableAddonsForPurchase,
} from "../../hooks/useAddons";
import useOrderByReceiptNumber from "../../hooks/useOrderByReceiptNumber";
import StatusMessage from "../../theme/StatusMessage";
import * as autographPurchase from "../../util/autographPurchase";
import CertificateOfAuthenticityAddon from "./CertificateOfAuthenticityAddon";
import PrioritySigningAddon from "./PrioritySigningAddon";
import VideoMessageAddon from "./VideoMessageAddon";
import VideoRecordingAddon from "./VideoRecordingAddon";
import AccessoryAddon from "./AccessoryAddon";

const loadingUI = (
  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
    <CircularProgress />
  </Box>
);

const errorUI = (
  <Box display="flex" justifyContent="center" alignItems="center" height="100%">
    <StatusMessage message="Unable to load addons" type="error" />
  </Box>
);

const StyledBox = styled("div")(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))",
  gap: theme.spacing(1),

  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const addonMap = {
  vipAddon: {
    Component: PrioritySigningAddon,
    has: autographPurchase.hasVipAddon,
    hasPurchased: (ap) => ap.vip_addon_price > 0 || ap.vipAddonPrice > 0,
  },
  frameAddon: {
    Component: AccessoryAddon,
    has: autographPurchase.hasFrameAddon,
  },
  coaAddon: {
    Component: CertificateOfAuthenticityAddon,
    has: autographPurchase.hasCoaAddon,
  },
  videoAddon: {
    Component: VideoRecordingAddon,
    has: autographPurchase.hasVideoAddon,
  },
  videoMessageAddon: {
    Component: VideoMessageAddon,
    has: autographPurchase.hasVideoMessageAddon,
  },
  topLoader: {
    Component: AccessoryAddon,
    has: autographPurchase.hasTopLoader,
  },
  softShellCase: {
    Component: AccessoryAddon,
    has: autographPurchase.hasSoftShellCase,
  },
  hardShellCase: {
    Component: AccessoryAddon,
    has: autographPurchase.hasHardShellCase,
  },
  blackCardboardFrame: {
    Component: AccessoryAddon,
    has: autographPurchase.hasBlackCardboardFrame,
  },
  magnetHolder: {
    Component: AccessoryAddon,
    has: autographPurchase.hasMagnetHolder,
  },
};

export default function Addons({ autographPurchaseId, receiptNumber }) {
  const {
    addons,
    isLoading: isLoadingAddons,
    isError: isErrorAddons,
  } = useAddons();

  const {
    order,
    isLoading: isLoadingOrder,
    isError: isErrorOrder,
    refresh: refreshOrder,
  } = useOrderByReceiptNumber(receiptNumber);

  const {
    availableAddons,
    isLoading: isLoadingAvailableAddons,
    isError: isErrorAvailableAddons,
    refresh: refreshAddons,
  } = useAvailableAddonsForPurchase(autographPurchaseId);

  const isLoading =
    isLoadingOrder || isLoadingAddons || isLoadingAvailableAddons;

  const isError = isErrorOrder || isErrorAddons || isErrorAvailableAddons;

  const user = useUser();

  const wrappedOnPurchase = React.useCallback(() => {
    // We want all instance of this event to be the same, so that every item's
    // addons get refreshed when one is purchased.
    document.dispatchEvent(
      new CustomEvent("addon_purchase::purchase_complete"),
    );
  }, []);

  React.useEffect(() => {
    const refreshAll = () => {
      refreshOrder();
      refreshAddons();
    };

    document.addEventListener("addon_purchase::purchase_complete", refreshAll);

    return () => {
      document.removeEventListener(
        "addon_purchase::purchase_complete",
        refreshAll,
      );
    };
  }, [refreshAddons, refreshOrder]);

  const autographPurchase = React.useMemo(() => {
    if (!Array.isArray(order?.shipments)) return null;

    for (const shipment of order.shipments) {
      for (const item of shipment.items) {
        if (item.autographPurchaseId === autographPurchaseId) {
          return item;
        }
      }
    }
  }, [order, autographPurchaseId]);

  const isCancelled = Boolean(autographPurchase?.cancelled);

  const addonsList = React.useMemo(() => {
    if (!availableAddons || !addons || !autographPurchase) return [];

    return Object.entries(addons).reduce((result, [key, addon]) => {
      const mappedAddon = addonMap[key];

      if (!mappedAddon) {
        return result;
      }

      const { Component, has } = mappedAddon;

      const hasAddon = has(autographPurchase);

      const availableAddon = availableAddons[key];
      const isAvailable = Boolean(availableAddon);
      const canPurchase = !hasAddon && isAvailable;
      const hasPurchased = mappedAddon.hasPurchased?.(autographPurchase);

      if (canPurchase || hasAddon) {
        result.push(
          <Component
            key={`${autographPurchaseId}-${key}`}
            autographPurchaseId={autographPurchaseId}
            autographPurchase={autographPurchase}
            user={user}
            addon={availableAddon ?? addon}
            isCancelled={isCancelled}
            has={hasAddon}
            canPurchase={canPurchase}
            onPurchase={wrappedOnPurchase}
            hasPurchased={hasPurchased}
          />,
        );
      }

      return result;
    }, []);
  }, [
    user,
    addons,
    availableAddons,
    autographPurchase,
    isCancelled,
    autographPurchaseId,
    wrappedOnPurchase,
  ]);

  if (isLoading) {
    return loadingUI;
  }

  if (isError) {
    return errorUI;
  }

  return <StyledBox>{addonsList}</StyledBox>;
}

Addons.propTypes = {
  autographPurchaseId: PropTypes.number.isRequired,
  receiptNumber: PropTypes.string.isRequired,
  onPurchase: PropTypes.func,
};
